import React, { useRef, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";

const CASES = [
  {
    before:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/a61emss4_WhatsApp%20Image%202026-05-23%20at%206.25.35%20PM.jpeg",
    after:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/gy8v2t8j_WhatsApp%20Image%202026-05-23%20at%206.25.35%20PM%20%281%29.jpeg",
    label_es: "Corona estética de porcelana e.max",
    label_en: "e.max porcelain aesthetic crown",
    // Caso 1: centrar el 2do premolar (gap) en el antes
    before_pos: "25% 50%",
    before_zoom: 1.15,
    after_pos: "75% 50%",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/qezpmnxt_WhatsApp%20Image%202026-05-23%20at%206.25.31%20PM.jpeg",
    after:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/zp94knke_WhatsApp%20Image%202026-05-23%20at%206.25.35%20PM%20%282%29.jpeg",
    label_es: "Blanqueamiento dental",
    label_en: "Dental whitening",
    // Caso 2: bajar el antes para alinearlo con los dientes del después
    before_pos: "50% 85%",
    after_pos: "50% 50%",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/wgcaj4cc_WhatsApp%20Image%202026-05-25%20at%208.19.20%20PM.jpeg",
    after:
      "https://customer-assets.emergentagent.com/job_smile-transformation-10/artifacts/zmxyafzg_WhatsApp%20Image%202026-05-25%20at%208.19.14%20PM.jpeg",
    label_es: "Diseño de sonrisa con coronas y carillas estéticas e.max",
    label_en: "Smile design with e.max aesthetic crowns and veneers",
    fit: "contain",
  },
];

const BeforeAfterCard = ({ data, idx }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const drag = useRef(false);

  const move = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, x)));
  };

  return (
    <div
      data-testid={`before-after-${idx}`}
      ref={ref}
      onMouseDown={(e) => {
        drag.current = true;
        move(e.clientX);
      }}
      onMouseMove={(e) => drag.current && move(e.clientX)}
      onMouseUp={() => (drag.current = false)}
      onMouseLeave={() => (drag.current = false)}
      onTouchStart={(e) => {
        drag.current = true;
        move(e.touches[0].clientX);
      }}
      onTouchMove={(e) => drag.current && move(e.touches[0].clientX)}
      onTouchEnd={() => (drag.current = false)}
      className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#1A1A1A] select-none cursor-ew-resize group"
    >
      <img
        src={data.after}
        alt="after"
        className={`absolute inset-0 w-full h-full ${data.fit === "contain" ? "object-contain" : "object-cover"}`}
        style={{
          objectPosition: data.after_pos || "center",
          transform: `scale(${data.after_zoom || 1})`,
        }}
        draggable="false"
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={data.before}
          alt="before"
          className={`absolute inset-0 h-full ${data.fit === "contain" ? "object-contain" : "object-cover"}`}
          style={{
            width: `${100 / (pos / 100)}%`,
            maxWidth: "none",
            objectPosition: data.before_pos || "center",
            transform: `scale(${data.before_zoom || 1})`,
          }}
          draggable="false"
        />
      </div>

      <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase text-white bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
        {t.gallery.before}
      </span>
      <span className="absolute top-4 right-4 text-[10px] tracking-[0.25em] uppercase text-[#1A1A1A] bg-white/90 backdrop-blur px-3 py-1.5 rounded-full">
        {t.gallery.after}
      </span>

      <div className="ba-handle" style={{ left: `${pos}%` }} />

      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
        <span className="text-xs font-medium tracking-wide bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
          {lang === "es" ? data.label_es : data.label_en}
        </span>
      </div>
    </div>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  return (
    <section
      id="cases"
      data-testid="gallery-section"
      className="bg-white py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7 reveal">
            <p className="overline mb-6">— {t.gallery.overline}</p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
              {t.gallery.title}
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-[#666666] text-base leading-relaxed">
              {t.gallery.sub}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CASES.map((c, i) => (
            <div
              key={i}
              className="reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <BeforeAfterCard data={c} idx={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
